import React, { useState, useEffect, useRef, useCallback } from "react";
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  InputBase,
  Fab,
  ClickAwayListener,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import { Close, Mood, Check } from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { i18n } from "../../translate/i18n";

const WA_BG = "#ECE5DD";
const BUBBLE_OUT = "#DCF8C6";
const WA_GREEN = "#25D366";

const previewPattern = {
  backgroundColor: WA_BG,
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4ccc4' fill-opacity='0.35'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
  borderRadius: 8,
  padding: "16px 12px",
  minHeight: 120,
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  marginBottom: 16,
};

/** Coloca o cursor no fim do texto (focus() sozinho deixa o caret no início em vários browsers). */
const focusInputAtEnd = (inputEl, lengthHint) => {
  if (!inputEl || typeof inputEl.setSelectionRange !== "function") return;
  const len =
    lengthHint != null ? lengthHint : String(inputEl.value ?? "").length;
  inputEl.focus();
  try {
    inputEl.setSelectionRange(len, len);
  } catch (_) {
    /* ignore */
  }
};

const EditMessageModal = ({ open, message, loading, onClose, onSubmit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [editText, setEditText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && message) {
      const text = message.body != null ? String(message.body) : "";
      setEditText(text);
      setShowEmoji(false);
      setTimeout(() => focusInputAtEnd(inputRef.current, text.length), 150);
    }
  }, [open, message?.id, message?.body]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmoji = useCallback((emoji) => {
    const sym = emoji?.native || "";
    setEditText((prev) => (prev || "") + sym);
    setShowEmoji(false);
    setTimeout(() => {
      const el = inputRef.current;
      if (el) focusInputAtEnd(el, String(el.value ?? "").length);
    }, 10);
  }, []);

  const handleConfirm = async () => {
    if (loading) return;
    const trimmed = (editText || "").trim();
    if (!trimmed) return;
    await onSubmit(trimmed);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    }
  };

  let timeStr = "";
  try {
    if (message?.createdAt) {
      timeStr = format(parseISO(message.createdAt), "HH:mm");
    }
  } catch (_) {
    timeStr = "";
  }

  return (
    <Dialog
      open={open && Boolean(message)}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="edit-message-dialog-title"
      PaperProps={{
        style: {
          borderRadius: isMobile ? 0 : 12,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        id="edit-message-dialog-title"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 8px 12px 16px",
        }}
      >
        <span style={{ fontSize: "1.05rem", fontWeight: 500 }}>
          {i18n.t("messageOptionsMenu.editModal.title")}
        </span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ paddingTop: 0, paddingBottom: 16 }}>
        <Box style={previewPattern}>
          <Box
            style={{
              maxWidth: "88%",
              backgroundColor: BUBBLE_OUT,
              color: "#303030",
              padding: "8px 10px 6px 10px",
              borderRadius: 8,
              boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              fontSize: "0.95rem",
              lineHeight: 1.4,
            }}
          >
            {editText || "\u00a0"}
            <Box
              component="span"
              style={{
                display: "inline-block",
                marginLeft: 6,
                fontSize: "0.7rem",
                color: "rgba(0,0,0,0.45)",
                verticalAlign: "bottom",
              }}
            >
              {timeStr}
              {message?.isEdited ? ` · ${i18n.t("messageOptionsMenu.editModal.edited")}` : ""}
            </Box>
          </Box>
        </Box>

        <Box
          style={{
            display: "flex",
            alignItems: "flex-end",
            borderBottom: `2px solid ${WA_GREEN}`,
            paddingBottom: 4,
            marginBottom: 8,
          }}
        >
          <InputBase
            inputRef={inputRef}
            multiline
            minRows={2}
            maxRows={8}
            fullWidth
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            placeholder={i18n.t("messageOptionsMenu.editModal.placeholder")}
            style={{ fontSize: "1rem", lineHeight: 1.45 }}
          />
        </Box>

        <Box
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 8,
            position: "relative",
          }}
        >
          <Box style={{ position: "relative" }}>
            <IconButton
              aria-label="emoji"
              onClick={() => setShowEmoji((v) => !v)}
              disabled={loading}
              size="small"
            >
              <Mood style={{ color: "#8696a0" }} />
            </IconButton>
            {showEmoji && (
              <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                <Box
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    right: 0,
                    zIndex: 1400,
                    marginBottom: 8,
                  }}
                >
                  <Picker
                    perLine={8}
                    theme={theme.palette.type === "dark" ? "dark" : "light"}
                    i18n={i18n}
                    showPreview
                    showSkinTones={false}
                    onSelect={handleEmoji}
                  />
                </Box>
              </ClickAwayListener>
            )}
          </Box>

          <Fab
            size="small"
            aria-label={i18n.t("messageOptionsMenu.editModal.confirm")}
            onClick={() => handleConfirm()}
            disabled={loading || !(editText || "").trim()}
            style={{
              backgroundColor: WA_GREEN,
              color: "#fff",
              boxShadow: "none",
            }}
          >
            {loading ? (
              <CircularProgress size={22} style={{ color: "#fff" }} />
            ) : (
              <Check style={{ fontSize: 22 }} />
            )}
          </Fab>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditMessageModal;
